// Note that for each flag you intend to use here, you must
// enable this flag to be used with the client SDK
// inside of the Settings section of the given flag, otherwise
// the flag simply won't be returned to the client
export enum Flag {
  // Setting the Timers feature's minimum allowed interval
  TIMERS_MIN_INTERVAL = "ui.timers-min-interval",

  // Overrides SHOW_CONTROL_FLOW regardless of OPA configuration
  OVERRIDE_CONTROL_FLOW_ON = "ui.control-flow-override-on",
  // Enable / disable the AI Assistant feature in code editors
  ENABLE_AI_ASSISTANT = "ui.ai-assistant-enabled",

  // enable / disable passing metadata to AI assistant
  ENABLE_AI_ASSISTANT_METADATA = "ui.ai-assistant-metadata-enabled",

  // for orgs that still use Global.URL.queryParams.environment in app
  ENABLE_PROFILES_WITH_ENV = "superblocks.profiles.settings-enabled-with-env",

  ENABLE_VALIDATE_SUBJECT_TOKEN = "agent.plugins.auth.validate_subject_token_during_obo_flow.enabled",

  PARALLEL_POOL_MAX_SIZE = "agent.quota.api.parallel.pool.size.max",

  ENABLE_SUPERBLOCKS_SUPPORT_UPDATES = "server.rbac.superblocks-support-updates-enabled",

  LAYOUTS_ENABLE_MARGIN = "ui.layouts.enable-margin-padding",

  LAYOUTS_ENABLE_GROUPING = "ui.layouts.enable-group-shortcut",

  ENABLE_PADDING_ADVANCED_MODE = "ui.padding.enable-advanced-mode",

  ENABLE_SECRETS_MANAGEMENT = "ui.enable-secrets-management",

  ENABLE_TABLE_IMAGE_SIZE = "ui.enable-table-image-size",

  ENABLE_RESOURCE_SIGNING = "ui.enable-resource-signing",

  // Show experimental integration fields
  INTEGRATION_ENABLE_EXPERIMENTAL = "ui.integration.enable-experimental",

  // Enable scoped catalog/schemas for Databricks integrations
  INTEGRATIONS_DATABRICKS_SCOPED = "integrations.databricks.scoped",

  ENABLE_APP_CLONE = "ui.enable-app-clone",

  ENABLE_EMBED = "ui.enable-embed",

  ENABLE_ACCESS_TOKENS_PAGE = "ui.enable-access-tokens-page",

  ACCESS_TOKEN_MAX_EXPIRATION_SECONDS = "server.org.token.max.expiration.seconds",

  ENABLE_MULTIPAGE = "ui.multi-page.enabled",
  MULTIPAGE_SLIDEOUTS = "ui.multi-page.slideouts",
  MULTIPAGE_ROUTE_EVENTS = "ui.multi-page.route-events",

  ENABLE_ORG_MIGRATE_TO_EU_BANNER = "ui.banner.org-migrated-to-eu.enabled",

  SALES_SETTINGS_JSON = "superblocks.sales_settings.json",
  SALES_BILLING_EMAIL = "superblocks.sales_billing_email",

  ENABLE_CUSTOM_EVENTS = "ui.enable-custom-events",

  CODE_FORMATTING = "ui.code-editor.enable-code-formatting",

  ENABLE_TEMPLATE = "ui.enable-template",

  ENABLE_MENU_WIDGET = "ui.widgets.menu.enabled",

  ENABLE_LINK_WIDGET = "ui.widgets.link.enabled",

  ENABLE_KEY_VALUE_WIDGET = "ui.widgets.keyvalue.enabled",

  ENABLE_DEPLOY_CONFIRMATION = "ui.enable-deploy-confirmation",

  ENABLE_APP_SCOPE = "ui.app-scope.enabled",

  DISABLE_LOAD_METADATA_FOR_INTEGRATIONS = "ui.disable-load-metadata-for-integrations",

  ENABLE_INTERCOM_LIVE_CHAT = "ui.intercom-live-chat.enabled",

  ENABLE_RBAC_V2 = "superblocks.rbac.v2.server.enforce-authz",

  ENABLE_CUSTOM_PAGE_LOAD_ACTIONS = "ui.custom-page-load-actions.enabled",

  UI_BLOCKS_FOR_USERS = "ui.ui-blocks.enabled", // Used by Superblocks users (end-users & creators)
  UI_BLOCKS_ADMINISTRATION = "ui-blocks.administration",
  UI_BLOCKS_CUSTOMER_BLOCKS = "ui.ui-blocks.customer-blocks",

  ENABLE_REFERENCE_ANALYSIS = "ui.reference-analysis.enabled",
  UI_BLOCKS_BACKEND = "ui-blocks.use-server", // use the real backend
  UI_ADD_COMPONENT_FLOW = "ui.add-component-flow.enabled",

  UI_AI_ASSISTANT_CONTEXT_ENABLED = "ui.ai-assistant.context-enabled",
  UI_AI_CODE_COMPLETION_CONTEXT_MODE_OVERRIDE = "ui.ai.code-completion.context_mode_override",
  UI_AI_CODE_COMPLETION_ENABLED = "ui.ai.code-completion.enabled",
  ENABLE_AI_COMPONENT_EDITING = "ui.ai-component-editing.enabled",
  ENABLE_AI_COMPONENT_EDITING_AUDIO_TRANSCRIPTION = "ui.ai-component-editing-audio-transcription.enabled",
  SHOW_AI_DISCARD_FEEDBACK = "ui.ai-component-editing.show-discard-feedback",

  // TODO(pbardea): This blog of flags are temporary flags and should be phased
  // out by end of 2024.
  UI_AI_CODE_COMPLETION_FEEDBACK_ENABLED = "ui.ai.code-completion.feedback-enabled",
  AI_CODE_COMPLETION_ENABLED_CONFIG = "ui.ai.code-completion.enable_config",
  UI_SHOW_CONTEXT_MODES = "ui.ai.code-completion.show_context_modes",
  UI_AI_IN_BETA = "ui.ai.code-completion.is_beta",
  UI_AI_CODE_COMPLETION_DEBUG_LOGGING_ENABLED = "ui.ai.code-completion.debug-logging-enabled",
  UI_AI_CODE_COMPLETION_PREFIX_THRESHOLD = "ui.ai.code-completion.prefix-threshold",
  UI_AI_CODE_COMPLETION_AVG_THRESHOLD = "ui.ai.code-completion.avg-threshold",
  UI_AI_CODE_COMPLETION_SCORE_THRESHOLD = "ui.ai.code-completion.score-threshold",
  UI_AI_CODE_COMPLETION_PREFIX_THRESHOLD_TIER_2 = "ui.ai.code-completion.prefix-threshold-tier-2",
  UI_AI_CODE_COMPLETION_AVG_THRESHOLD_TIER_2 = "ui.ai.code-completion.avg-threshold-tier-2",

  ENABLE_PROGRAMMATIC_TABLE = "ui.programmatic-table.enabled",

  ENABLE_EMAIL_PLUGIN = "org.email.plugin.enabled",

  ENABLE_DEEP_BINDINGS_PATHS = "ui.use-deep-bindings-paths.enabled",

  AI_SERVICE_URL = "ui.ai-service-url",

  ENABLE_OAUTH_TOKEN_EXCHANGE = "ui.oauth-token-exchange.enabled",

  EVALUATOR_CACHE_DERIVED_PROPERTIES = "ui.evaluator.cache-derived-properties",

  ENABLE_HOME_PAGE_BULK_EDIT = "ui.enable-home-page-bulk-edit",

  HIDE_SUPERBLOCKS_HEADER = "ui.hide-superblocks-header",

  AI_MULTI_COMPONENT_EDITING = "ui.ai-multi-component-editing.enabled",

  AI_GCN_DEMO_INTEGRATION_IDS = "ai.gcn-demo-integration-ids",

  AI_ACTION_PROCESSING_DELAY = "ai.clark-action-processing-delay",

  ENABLE_OPTIMIZED_PAGE_LOAD = "ui.optimized-page-load.enabled",

  ALLOW_DEV_ENV = "superblocks.dev-env.enabled",

  SHOW_CODE_MODE_APP_BTN = "ui.code-mode-app-btn.enabled",

  DISABLE_LEGACY_APPS = "ui.disable-legacy-apps",

  CLOUDPREM_INTERCOM = "cloudprem.intercom",
  CLOUDPREM_WORKFLOWS = "cloudprem.workflows",
  CLOUDPREM_SCHEDULED_JOBS = "cloudprem.scheduledjobs",

  // Enable workflows for V2 orgs (for fetch credentials feature)
  V2_ORG_ENABLE_WORKFLOWS = "org.v2.enable-workflows-for-fetch-credentials",

  AI_FACTS_ENABLED = "ai.facts.enabled",
  AI_DASHBOARD_FILE_UPLOAD_ENABLED = "ai.dashboard-file-upload.enabled",
  AI_DASHBOARD_CHAT_LLMOBS_ENABLED = "ai.dashboard-chat.llmobs.enabled",
  AI_FILE_UPLOAD_ENABLED = "ai.file-upload.enabled",
  CLARK_ATTACHMENT_DOWNLOAD_ENABLED = "ai.clark-attachment-download.enabled",

  AI_APP_KNOWLEDGE_CHAT_ENABLED = "ai.app-knowledge.enabled.chat",

  // Enable Superblocks 2.0 view toggle for V1 orgs
  V1_ORG_ENABLE_SUPERBLOCKS_2_VIEW = "org.v1.enable-superblocks-2-view",

  // Set the default Superblocks 2.0 view for V1 orgs (works with or without toggle)
  V1_ORG_DEFAULT_SUPERBLOCKS_2_VIEW = "org.v1.default-superblocks-2-view",

  // Enable the Data Tags (Environments) view to replace Profiles
  ENABLE_DATA_TAGS_VIEW = "settings.enable-data-tags-view",

  // Show file upload UI on the Clark AI homepage (requires CLARK_AI_HOMEPAGE as well)
  HOMEPAGE_FILE_UPLOAD = "homepage.file-upload",

  // Integration tagging on the Clark AI homepage prompt (requires CLARK_AI_HOMEPAGE as well)
  HOMEPAGE_TAGGING = "homepage.tagging",

  // Homepage create menu + Clark template; see homepage-app-mode.ts (only-2, both-2-and-3, default-3, only-3)
  HOMEPAGE_APP_MODE = "ui.homepage-app.mode",

  ENABLE_SUGGEST_INTEGRATIONS_BANNER = "ui.suggest-integrations.banner",

  SDK_API_ENABLED = "clark.sdk-api.enabled",

  // Billing usage page
  BILLING_USAGE_PAGE_ENABLED = "billing.usage-page.enabled",

  // Route execute requests through the data plane gateway endpoint
  ENABLE_DATA_PLANE_GATEWAY = "ui.data-plane-gateway.enabled",

  // Credit card gate: block trial orgs until they enter a credit card
  ENABLE_CREDIT_CARD_GATE = "superblocks.billing.credit-card-gate.enabled",

  AUDIT_V2_ENABLED = "superblocks.audit.v2.enabled",

  ENABLE_INTRODUCING_CLARK_MODAL = "ui.introducing-clark-modal.enabled",

  // Enable grid view on the /apps page (thumbnail cards instead of list)
  HOMEPAGE_GRID_VIEW = "homepage.grid-view",

  // Join org gate: show new users a gate to request joining an existing org with matching domain
  ENABLE_JOIN_ORG_GATE = "superblocks.onboarding.join-org-gate.enabled",
}

export type FlagType = boolean | number | string | Record<string, any>;

export type AllFlags = {
  [K in Flag]: FlagType;
};
export type Flags = Partial<AllFlags>;

export const defaults = {
  [Flag.TIMERS_MIN_INTERVAL]: 5000,
  [Flag.ENABLE_PROFILES_WITH_ENV]: false,
  [Flag.OVERRIDE_CONTROL_FLOW_ON]: false,
  [Flag.ENABLE_AI_ASSISTANT]: false,
  [Flag.ENABLE_AI_ASSISTANT_METADATA]: true,
  [Flag.PARALLEL_POOL_MAX_SIZE]: 50,
  [Flag.ENABLE_SUPERBLOCKS_SUPPORT_UPDATES]: false,
  [Flag.ENABLE_PADDING_ADVANCED_MODE]: false,
  [Flag.ENABLE_SECRETS_MANAGEMENT]: false,
  [Flag.ENABLE_TABLE_IMAGE_SIZE]: false,
  [Flag.ENABLE_RESOURCE_SIGNING]: false,
  [Flag.INTEGRATION_ENABLE_EXPERIMENTAL]: false,
  [Flag.INTEGRATIONS_DATABRICKS_SCOPED]: false,
  [Flag.ENABLE_APP_CLONE]: true, // this will only be disabled for customers using application signing
  [Flag.ENABLE_EMBED]: false,
  [Flag.ENABLE_ACCESS_TOKENS_PAGE]: false,
  [Flag.ACCESS_TOKEN_MAX_EXPIRATION_SECONDS]: 31536000, // 1 year
  [Flag.ENABLE_MULTIPAGE]: false,
  [Flag.MULTIPAGE_SLIDEOUTS]: false,
  [Flag.MULTIPAGE_ROUTE_EVENTS]: false,
  [Flag.ENABLE_ORG_MIGRATE_TO_EU_BANNER]: false,
  [Flag.ENABLE_CUSTOM_EVENTS]: false,
  [Flag.SALES_SETTINGS_JSON]: {
    app_block_reason: null,
    block_config: {
      title: null,
      message: null,
      closable: null,
    },
  },
  [Flag.SALES_BILLING_EMAIL]: "billing-requests@superblockshq.com",
  [Flag.LAYOUTS_ENABLE_MARGIN]: false,
  [Flag.LAYOUTS_ENABLE_GROUPING]: false,
  [Flag.CODE_FORMATTING]: false,
  [Flag.ENABLE_TEMPLATE]: false,
  [Flag.ENABLE_MENU_WIDGET]: false,
  [Flag.ENABLE_LINK_WIDGET]: false,
  [Flag.ENABLE_KEY_VALUE_WIDGET]: false,
  [Flag.ENABLE_DEPLOY_CONFIRMATION]: true,
  [Flag.ENABLE_APP_SCOPE]: false,
  [Flag.DISABLE_LOAD_METADATA_FOR_INTEGRATIONS]: [],
  [Flag.ENABLE_INTERCOM_LIVE_CHAT]: false,
  [Flag.ENABLE_RBAC_V2]: false,
  [Flag.ENABLE_CUSTOM_PAGE_LOAD_ACTIONS]: false,
  [Flag.UI_BLOCKS_FOR_USERS]: false,
  [Flag.UI_BLOCKS_ADMINISTRATION]: false,
  [Flag.UI_BLOCKS_CUSTOMER_BLOCKS]: false,
  [Flag.ENABLE_REFERENCE_ANALYSIS]: false,
  [Flag.UI_BLOCKS_BACKEND]: false,
  [Flag.UI_ADD_COMPONENT_FLOW]: false,
  [Flag.UI_AI_ASSISTANT_CONTEXT_ENABLED]: false,
  [Flag.AI_CODE_COMPLETION_ENABLED_CONFIG]: {},
  [Flag.UI_AI_CODE_COMPLETION_ENABLED]: false,
  [Flag.UI_AI_CODE_COMPLETION_PREFIX_THRESHOLD]: 0.1,
  [Flag.UI_AI_CODE_COMPLETION_AVG_THRESHOLD]: 0.3,
  [Flag.UI_AI_CODE_COMPLETION_SCORE_THRESHOLD]: -3.0,
  [Flag.UI_AI_CODE_COMPLETION_PREFIX_THRESHOLD_TIER_2]: 0.4,
  [Flag.UI_AI_CODE_COMPLETION_AVG_THRESHOLD_TIER_2]: 0.45,
  [Flag.ENABLE_AI_COMPONENT_EDITING]: false,
  [Flag.ENABLE_AI_COMPONENT_EDITING_AUDIO_TRANSCRIPTION]: false,
  [Flag.SHOW_AI_DISCARD_FEEDBACK]: false,
  [Flag.ENABLE_PROGRAMMATIC_TABLE]: false,
  [Flag.ENABLE_EMAIL_PLUGIN]: false,
  [Flag.ENABLE_DEEP_BINDINGS_PATHS]: false,
  [Flag.UI_AI_IN_BETA]: false,
  [Flag.UI_SHOW_CONTEXT_MODES]: false,
  [Flag.UI_AI_CODE_COMPLETION_FEEDBACK_ENABLED]: false,
  [Flag.UI_AI_CODE_COMPLETION_DEBUG_LOGGING_ENABLED]: false,
  [Flag.UI_AI_CODE_COMPLETION_CONTEXT_MODE_OVERRIDE]: "",
  [Flag.AI_SERVICE_URL]: "https://ai.staging.superblocks.com/creator/systems",
  [Flag.ENABLE_OAUTH_TOKEN_EXCHANGE]: false,
  [Flag.EVALUATOR_CACHE_DERIVED_PROPERTIES]: false,
  [Flag.ENABLE_HOME_PAGE_BULK_EDIT]: false,
  [Flag.HIDE_SUPERBLOCKS_HEADER]: false,
  [Flag.AI_MULTI_COMPONENT_EDITING]: false,
  [Flag.ENABLE_OPTIMIZED_PAGE_LOAD]: false,
  [Flag.AI_GCN_DEMO_INTEGRATION_IDS]: {},
  [Flag.AI_ACTION_PROCESSING_DELAY]: 500,
  [Flag.ALLOW_DEV_ENV]: false,
  [Flag.SHOW_CODE_MODE_APP_BTN]: false,
  [Flag.DISABLE_LEGACY_APPS]: false,
  [Flag.ENABLE_VALIDATE_SUBJECT_TOKEN]: true,
  [Flag.CLOUDPREM_INTERCOM]: true,
  [Flag.CLOUDPREM_WORKFLOWS]: true,
  [Flag.CLOUDPREM_SCHEDULED_JOBS]: true,
  [Flag.AI_FACTS_ENABLED]: false,
  [Flag.AI_DASHBOARD_FILE_UPLOAD_ENABLED]: false,
  [Flag.AI_DASHBOARD_CHAT_LLMOBS_ENABLED]: true,
  [Flag.AI_FILE_UPLOAD_ENABLED]: false,
  [Flag.CLARK_ATTACHMENT_DOWNLOAD_ENABLED]: false,
  [Flag.AI_APP_KNOWLEDGE_CHAT_ENABLED]: false,
  [Flag.V2_ORG_ENABLE_WORKFLOWS]: false,
  [Flag.V1_ORG_ENABLE_SUPERBLOCKS_2_VIEW]: false,
  [Flag.V1_ORG_DEFAULT_SUPERBLOCKS_2_VIEW]: false,
  [Flag.ENABLE_DATA_TAGS_VIEW]: false,
  [Flag.HOMEPAGE_FILE_UPLOAD]: false,
  [Flag.HOMEPAGE_TAGGING]: false,
  [Flag.HOMEPAGE_APP_MODE]: "",
  [Flag.ENABLE_SUGGEST_INTEGRATIONS_BANNER]: false,
  [Flag.SDK_API_ENABLED]: false,
  [Flag.BILLING_USAGE_PAGE_ENABLED]: false,
  [Flag.ENABLE_DATA_PLANE_GATEWAY]: false,
  [Flag.ENABLE_CREDIT_CARD_GATE]: false,
  [Flag.AUDIT_V2_ENABLED]: false,
  [Flag.ENABLE_INTRODUCING_CLARK_MODAL]: false,
  [Flag.HOMEPAGE_GRID_VIEW]: false,
  [Flag.ENABLE_JOIN_ORG_GATE]: false,
} satisfies AllFlags;
